/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import type { Maybe } from '__generated__/graphql-types';

import { Button, Grid, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type {
  BasicCardFragment,
  EntWebsiteBlockFeaturedContentQuery,
  EntWebsiteFeaturedContentFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import Image from 'bundles/ent-website/contentful/core/Image';
import { entWebsiteBlockFeaturedContentQuery } from 'bundles/ent-website/queries/EntWebsiteFeaturedContent';
import { compactBlockStyle } from 'bundles/ent-website/shared-styles';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/ent-website';

export type Props = BlockModuleInnerProps<EntWebsiteFeaturedContentFragment>;

const styles = {
  mainCardContainer: css`
    padding-right: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      padding-right: 0;
    }
  `,
  mainCard: css`
    border-radius: var(--cds-spacing-100);
    background-color: var(--cds-color-blue-100);
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
  `,
  mainCardFooter: (theme: Theme, hasCta = false) => css`
    padding: ${theme.spacing(32, 24, hasCta ? 12 : 32, 24)};
    background-color: var(--cds-color-grey-25);
  `,
  mainCardHeadline: css`
    font-size: 32px;
    font-weight: 600;
    letter-spacing: -0.96px;
    margin-bottom: var(--cds-spacing-100);

    ${breakpoints.down('sm')} {
      font-size: 20px;
      letter-spacing: -0.6px;
    }
  `,
  mainCardBody: css`
    font-size: 24px;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: -0.48px;

    ${breakpoints.down('sm')} {
      font-size: 16px;
    }
  `,
  mainCardCtaWrapper: css`
    margin-top: var(--cds-spacing-100);
  `,
  image: css`
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    aspect-ratio: 16/9;
    object-fit: cover;
    border-radius: var(--cds-spacing-100) var(--cds-spacing-100) 0 0;
    flex: 1;

    ${breakpoints.down('sm')} {
      height: unset;
    }
  `,
  featuredCardsContainer: css`
    position: relative;
    max-width: 100%;
  `,
  featuredCards: css`
    max-height: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    right: 0;
    flex-wrap: nowrap;
    padding-left: var(--cds-spacing-300);

    @media not (pointer: coarse) {
      ::-webkit-scrollbar-thumb {
        background-color: var(--cds-color-neutral-primary);
        border-radius: 10px;
        cursor: pointer;
      }

      ::-webkit-scrollbar {
        width: 8px;
        cursor: pointer;
      }

      ::-webkit-scrollbar-track {
        background: var(--cds-color-grey-25);
        border: thin solid lightgray;
        box-shadow: 0 0 3px var(--cds-color-grey-25) inset;
        border-radius: 10px;
      }

      ::-webkit-scrollbar-thumb:horizontal {
        background: var(--cds-color-grey-500);
        border-radius: 10px;
        height: 8px;
      }

      ::-webkit-scrollbar-thumb:hover {
        background: var(--cds-color-grey-600);
      }
    }

    ${breakpoints.down('sm')} {
      position: initial;
      margin-top: var(--cds-spacing-300);
      flex-direction: row;
      overflow: auto;
      height: auto;
      padding-left: 0;
    }
  `,
  featuredCardWrapper: css`
    display: flex;
    flex-direction: row;
    flex: 1;
    width: 100%;
    margin: var(--cds-spacing-300) var(--cds-spacing-150) var(--cds-spacing-300) 0;

    :first-of-type {
      margin-top: 0;
    }

    :last-of-type {
      margin-bottom: 0;
    }

    ${breakpoints.down('sm')} {
      flex-direction: column;
      margin: 0 12px;

      :first-of-type {
        margin-left: 0;
      }

      :last-of-type {
        margin-right: 0;
      }
    }
  `,
  featuredCardImage: (_: Theme, imageUrl: string) => css`
    height: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 8px;
    overflow: hidden;
    background-image: url('${imageUrl}');
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;

    ${breakpoints.down('sm')} {
      height: unset;
      width: 100%;
      min-width: 220px;
    }
  `,
  featuredCardBody: css`
    flex: 1;
    flex-wrap: nowrap;
    padding: var(--cds-spacing-150) 0 var(--cds-spacing-150) var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200) 0 var(--cds-spacing-100);
    }
  `,
  featuredCardEyebrow: css`
    color: var(--cds-color-neutral-primary-weak);
    font-size: 14px;
    font-weight: 700;
    line-height: 140%;
    min-height: 1rem;
  `,
  featuredCardHeadlineWrapper: css`
    flex: 1;
  `,
  featuredCardHeadline: css`
    display: flex;
    flex-grow: 1;
    /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-100) 0;
    color: var(--cds-color-neutral-primary);
    font-size: 22px;
    font-weight: 600;
    line-height: 26.4px;
    height: 79.2px;
    letter-spacing: -0.44px;

    ${breakpoints.down('sm')} {
      font-size: 16px;
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  featuredCardCta: css`
    margin-left: -8px;
    background: none !important;
  `,
  card: css`
    text-decoration: none !important;

    :hover {
      button {
        text-decoration: underline;
      }
    }
  `,
};

type MainCardProps = {
  mainCard?: Maybe<BasicCardFragment>;
};

export const MainCard = ({ mainCard }: MainCardProps) => {
  const theme = useTheme();

  return mainCard?.cta?.ctaTarget ? (
    <Grid
      component={TrackedLink2}
      trackingName="main_card"
      css={[styles.mainCard, styles.card]}
      href={mainCard?.cta?.ctaTarget}
      target={mainCard?.cta?.shouldOpenInNewWindow ? '_blank' : undefined}
      rel="noreferrer"
    >
      {mainCard?.media && <Image css={styles.image} content={mainCard?.media} />}
      <Grid
        container
        direction="column"
        css={styles.mainCardFooter(theme, !!mainCard?.cta?.ctaTarget && !!mainCard.cta.ctaText)}
      >
        <Typography variant="d2semibold" css={styles.mainCardHeadline}>
          {mainCard?.headline}
        </Typography>
        <Typography2 component="p" css={styles.mainCardBody}>
          {mainCard?.body}
        </Typography2>
        {mainCard?.cta?.ctaTarget && mainCard.cta.ctaText && (
          <Grid container justifyContent="flex-end" css={styles.mainCardCtaWrapper}>
            <Button variant="ghost" icon={<ArrowNextIcon />}>
              {mainCard.cta.ctaText}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  ) : null;
};

export function FeaturedContentModule({ content, assembly = {} }: Props) {
  const theme = useTheme();
  const { mainCard, featuredResourceCardsCollection } = content;

  if (!content) {
    return null;
  }

  const contentPanel = () => (
    <Grid container>
      <Grid item md={6} sm={12} css={styles.mainCardContainer}>
        <MainCard mainCard={mainCard} />
      </Grid>
      <Grid item md={6} sm={12} css={styles.featuredCardsContainer}>
        <Grid container css={styles.featuredCards}>
          {featuredResourceCardsCollection?.items?.map(
            (item) =>
              item &&
              item?.image?.url &&
              item?.cta?.ctaTarget && (
                <Grid
                  component={TrackedLink2}
                  data={{ slug: item?.slug }}
                  trackingName="featured_content_card"
                  key={item?.slug}
                  css={[styles.featuredCardWrapper, styles.card]}
                  href={item?.cta?.ctaTarget}
                  target={item?.cta?.shouldOpenInNewWindow ? '_blank' : undefined}
                  rel="noreferrer"
                >
                  <div css={styles.featuredCardImage(theme, item?.image?.url)} />
                  <Grid container alignItems="flex-start" direction="column" css={styles.featuredCardBody}>
                    <Typography2 component="p" css={styles.featuredCardEyebrow}>
                      {item?.eyebrow}
                    </Typography2>
                    <Grid container css={styles.featuredCardHeadlineWrapper}>
                      <Typography2 component="p" css={styles.featuredCardHeadline}>
                        {item?.headline}
                      </Typography2>
                    </Grid>

                    <Button
                      variant="ghost"
                      icon={<ArrowNextIcon />}
                      iconPosition="after"
                      size="small"
                      css={styles.featuredCardCta}
                    >
                      {item?.cta?.ctaText ?? _t('Read More')}
                    </Button>
                  </Grid>
                </Grid>
              )
          )}
        </Grid>
      </Grid>
    </Grid>
  );

  return <BlockLayout blockStyle={compactBlockStyle} assembly={noImage(assembly)} renderContent={contentPanel} />;
}

export const BlockFeaturedContentModule = withBlockQuery<
  EntWebsiteFeaturedContentFragment,
  // @ts-expect-error TSMIGRATION-5.0 TS2344
  EntWebsiteBlockFeaturedContentQuery
>(entWebsiteBlockFeaturedContentQuery)(FeaturedContentModule);
